import { apiClient } from '../../lib/apiClient';
import type {
  AdminAccountPlanResult,
  AdminAccountPlanUpdate,
  AdminAccountRead,
  AdminBusinessDetailRead,
  AdminBusinessRead,
  AdminCommunityTemplateRead,
  AdminCommunityTemplateReject,
  AdminFlagRead,
  AdminFlagResolve,
  AdminPlanStatsRead,
  AdminQuotaUpdate,
  AdminStatusRead,
  CommunityTemplateStatus,
  FlagStatus,
  LearningMetricsRead,
  QuotaRead,
} from '../../types/api';
import type { RequestPageQuery } from '../generation/generationApi';

/**
 * The operator surface. Top-level routes, not business-scoped: an operator is
 * not a member of the businesses they support, and the gate is a server-side
 * email allowlist plus the service-role key — never a claim in the caller's own
 * token.
 *
 * A caller who is not on that allowlist gets **404** from every route here,
 * including `/status`. That is the whole protocol: a 403 would tell an ordinary
 * user that an admin surface exists and that they merely lack the rank for it.
 *
 * Nothing here can change what a pipeline produced. Quotas, account plans and
 * flag verdicts are the only writes, and all are logged server-side.
 */

const ADMIN_PATH = '/api/v1/admin';

/** 200 means the caller is an operator; 404 means they are not. */
export function fetchAdminStatus(signal?: AbortSignal): Promise<AdminStatusRead> {
  return apiClient.get<AdminStatusRead>(`${ADMIN_PATH}/status`, { signal });
}

/** Every business in the deployment, newest first, with this month's usage. */
export function fetchAdminBusinesses(
  page: RequestPageQuery,
  signal?: AbortSignal,
): Promise<AdminBusinessRead[]> {
  return apiClient.get<AdminBusinessRead[]>(`${ADMIN_PATH}/businesses`, {
    query: { limit: page.limit, offset: page.offset },
    signal,
  });
}

/** One business, its recent requests and how many flags are waiting on it. */
export function fetchAdminBusiness(
  businessId: string,
  signal?: AbortSignal,
): Promise<AdminBusinessDetailRead> {
  return apiClient.get<AdminBusinessDetailRead>(`${ADMIN_PATH}/businesses/${businessId}`, {
    signal,
  });
}

/**
 * Sets or clears one business's monthly allowance, and returns the *effective*
 * quota — the number to read back to whoever asked for the change.
 *
 * `monthly_generation_quota: null` clears the override and returns the business
 * to the deployment default; `0` stops generation entirely. They are not the
 * same request.
 */
export function updateAdminQuota(businessId: string, body: AdminQuotaUpdate): Promise<QuotaRead> {
  return apiClient.put<QuotaRead>(`${ADMIN_PATH}/businesses/${businessId}/quota`, { body });
}

/** The flag queue for one status, newest first, each with its frozen snapshot. */
export function fetchAdminFlags(
  status: FlagStatus,
  page: RequestPageQuery,
  signal?: AbortSignal,
): Promise<AdminFlagRead[]> {
  return apiClient.get<AdminFlagRead[]>(`${ADMIN_PATH}/flags`, {
    query: { status, limit: page.limit, offset: page.offset },
    signal,
  });
}

/**
 * Closes an open flag as resolved (something was wrong) or dismissed (nothing
 * was). Both are terminal and both are logged.
 *
 * A flag that another operator already closed answers 409
 * `FLAG_ALREADY_RESOLVED` rather than overwriting the first verdict and note.
 */
export function resolveAdminFlag(flagId: string, body: AdminFlagResolve): Promise<AdminFlagRead> {
  return apiClient.post<AdminFlagRead>(`${ADMIN_PATH}/flags/${flagId}/resolve`, { body });
}

/** The statuses the review queue lists. `draft` is the author's alone and is not listable. */
export type ReviewableTemplateStatus = Exclude<CommunityTemplateStatus, 'draft'>;

/**
 * Community templates in one review status. `pending` comes oldest first (first
 * come, first reviewed); `approved` and `rejected` newest reviewed first.
 */
export function fetchAdminCommunityTemplates(
  status: ReviewableTemplateStatus,
  page: RequestPageQuery,
  signal?: AbortSignal,
): Promise<AdminCommunityTemplateRead[]> {
  return apiClient.get<AdminCommunityTemplateRead[]>(`${ADMIN_PATH}/community-templates`, {
    query: { status, limit: page.limit, offset: page.offset },
    signal,
  });
}

/** One community template as the reviewer sees it, with a fresh signed design URL. */
export function fetchAdminCommunityTemplate(
  templateId: string,
  signal?: AbortSignal,
): Promise<AdminCommunityTemplateRead> {
  return apiClient.get<AdminCommunityTemplateRead>(
    `${ADMIN_PATH}/community-templates/${templateId}`,
    { signal },
  );
}

/**
 * Publishes a `pending` template to the community gallery. Anything else is 409
 * `TEMPLATE_NOT_REVIEWABLE`; a failed copy of the design changes nothing.
 */
export function approveAdminCommunityTemplate(
  templateId: string,
): Promise<AdminCommunityTemplateRead> {
  return apiClient.post<AdminCommunityTemplateRead>(
    `${ADMIN_PATH}/community-templates/${templateId}/approve`,
  );
}

/**
 * Sends a template back with a note the author sees in the studio. From
 * `pending` this rejects; from `approved` it unpublishes (takes it out of the
 * gallery). The note is required, 1..500 chars (422 otherwise); any other
 * status is 409 `TEMPLATE_NOT_REVIEWABLE`.
 */
export function rejectAdminCommunityTemplate(
  templateId: string,
  body: AdminCommunityTemplateReject,
): Promise<AdminCommunityTemplateRead> {
  return apiClient.post<AdminCommunityTemplateRead>(
    `${ADMIN_PATH}/community-templates/${templateId}/reject`,
    { body },
  );
}

/** The window and optional brand the Learning tab asks about. */
export interface LearningMetricsQuery {
  /** `YYYY-MM-DD`, inclusive. */
  since: string;
  /** `YYYY-MM-DD`, inclusive. */
  until: string;
  /** Null = every brand. */
  businessId: string | null;
}

/**
 * Brand memory outcomes by arm for a window (at most 180 days, else 422), over
 * every brand or one. Read-only.
 */
export function fetchLearningMetrics(
  query: LearningMetricsQuery,
  signal?: AbortSignal,
): Promise<LearningMetricsRead> {
  return apiClient.get<LearningMetricsRead>(`${ADMIN_PATH}/learning-metrics`, {
    query: { since: query.since, until: query.until, business_id: query.businessId },
    signal,
  });
}

/**
 * Every account (owner profile) with its plan, where the plan came from, the
 * Paddle ids if any, and how many brands it owns.
 */
export function listAccounts(signal?: AbortSignal): Promise<AdminAccountRead[]> {
  return apiClient.get<AdminAccountRead[]>(`${ADMIN_PATH}/accounts`, { signal });
}

/**
 * Sets an account's plan by hand (`plan_source` is always `manual`) and returns
 * the account as it now stands. An omitted optional field is left unchanged;
 * `plan_limits_override: null` clears the custom caps.
 */
export function setAccountPlan(
  userId: string,
  body: AdminAccountPlanUpdate,
): Promise<AdminAccountPlanResult> {
  return apiClient.put<AdminAccountPlanResult>(`${ADMIN_PATH}/accounts/${userId}/plan`, { body });
}

/** Deployment-wide plan, trial and sign-up counts for the Accounts tab. Read-only. */
export function fetchPlanStats(signal?: AbortSignal): Promise<AdminPlanStatsRead> {
  return apiClient.get<AdminPlanStatsRead>(`${ADMIN_PATH}/plan-stats`, { signal });
}
